import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });

      setMessage(response.data.message);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <h1 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
          Reset Password
        </h1>
        <p className="text-gray-600 mb-6" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
          Enter your new password below.
        </p>

        {message && (
          <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
            <p className="text-green-800 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
              ✅ {message}
            </p>
            <p className="text-green-600 text-xs mt-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
              Redirecting to login...
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
            <p className="text-red-800 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
              {error}
            </p>
          </div>
        )}

        {!message && token && (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-gray-800"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
                placeholder="At least 6 characters"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 mb-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-gray-800"
                style={{ fontFamily: 'IBM Plex Serif, serif' }}
                placeholder="Re-enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-800 text-white py-3 rounded hover:bg-gray-900 transition-all disabled:opacity-50 font-semibold"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {!token && !message && (
          <div className="text-center">
            <a
              href="/forgot-password"
              className="text-blue-600 hover:underline"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Request a new reset link
            </a>
          </div>
        )}

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-blue-600 hover:underline text-sm"
            style={{ fontFamily: 'IBM Plex Serif, serif' }}
          >
            ← Back to Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
